import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ANNOUNCEMENTS } from '../../config/announcements';
import './AmbassadorEventPage.css';

export const AMBASSADOR_EVENT_ID = 'ambassador-season-2-2026-07';
export const AMBASSADOR_EVENT = ANNOUNCEMENTS.find(
  announcement => announcement.id === AMBASSADOR_EVENT_ID
);

export const isAmbassadorRecruitmentOpen = (
  now = Date.now(),
  event = AMBASSADOR_EVENT
) => {
  if (!event) return false;

  const timestamp = now instanceof Date ? now.getTime() : new Date(now).getTime();
  const startTimestamp = new Date(event.startDate).getTime();
  const endTimestamp = new Date(event.endDate).getTime();
  return timestamp >= startTimestamp && timestamp <= endTimestamp;
};

const trackAmbassadorEvent = (eventName, details = {}) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, {
    event_category: 'Ambassador_Recruitment',
    event_label: 'season_2_2026',
    ...details,
  });
};

const rememberApplication = () => {
  try {
    window.localStorage.setItem(
      `cardnews_${AMBASSADOR_EVENT_ID}_dismissed`,
      'true'
    );
  } catch {
    // 저장소가 차단되어도 구글폼 이동은 그대로 허용한다.
  }
};

const AmbassadorEventPage = ({ now = Date.now() }) => {
  const navigate = useNavigate();
  const recruitmentOpen = isAmbassadorRecruitmentOpen(now);

  useEffect(() => {
    document.documentElement.classList.add('ambassador-event-active');
    document.body.classList.add('ambassador-event-active');
    return () => {
      document.documentElement.classList.remove('ambassador-event-active');
      document.body.classList.remove('ambassador-event-active');
    };
  }, []);

  if (!AMBASSADOR_EVENT) return null;

  const handleApply = (position) => {
    rememberApplication();
    trackAmbassadorEvent('ambassador_apply_click', {
      button_position: position,
    });
  };

  const handleSocialClick = (link, index) => {
    trackAmbassadorEvent('ambassador_instagram_click', {
      link_index: index + 1,
      link_label: link.text,
    });
  };

  const renderApplyButton = (position) => {
    if (!recruitmentOpen) {
      return (
        <span aria-disabled="true" className="ambassador-event-apply ambassador-event-apply--disabled">
          모집이 마감됐어요
        </span>
      );
    }

    return (
      <a
        className="ambassador-event-apply"
        href={AMBASSADOR_EVENT.primaryButton.url}
        onClick={() => handleApply(position)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {AMBASSADOR_EVENT.primaryButton.text}
      </a>
    );
  };

  return (
    <div className="ambassador-event-page">
      <Helmet>
        <title>왓퀴즈 AI 앰버서더 2기 모집</title>
        <meta
          content="왓퀴즈 AI 앰버서더 2기 선생님 20명을 모집합니다. 6개월 동안 함께 배우고 만들며 교육의 변화를 만들어주세요."
          name="description"
        />
        <meta content="왓퀴즈 AI 앰버서더 2기 모집" property="og:title" />
        <meta
          content="PRO 1년 이용권과 강의 지원금을 받고 왓퀴즈의 성장을 함께 만들어주세요."
          property="og:description"
        />
        <meta
          content="https://whatquiz.co.kr/images/announcements/ambassador-s2/slide-1.jpg"
          property="og:image"
        />
        <meta content="https://whatquiz.co.kr/events/ambassador" property="og:url" />
        <meta content="website" property="og:type" />
        <link href="https://whatquiz.co.kr/events/ambassador" rel="canonical" />
      </Helmet>

      <main className="ambassador-event-main">
        <button
          className="ambassador-event-back"
          onClick={() => navigate('/events')}
          type="button"
        >
          <span aria-hidden="true">←</span>
          이벤트 목록
        </button>

        <header className="ambassador-event-hero">
          <span className={`ambassador-event-status${recruitmentOpen ? '' : ' ambassador-event-status--closed'}`}>
            {recruitmentOpen ? '8월 21일까지 · 20명 모집' : '모집 마감'}
          </span>
          <h1>왓퀴즈 AI 앰버서더 2기</h1>
          <p className="ambassador-event-lead">
            선생님의 목소리로 함께 배우고, 만들고, 성장해요.
          </p>

          <div aria-label="모집 핵심 정보" className="ambassador-event-facts" role="list">
            <div className="ambassador-event-fact" role="listitem">
              <strong>20명</strong>
              <span>모집 인원</span>
            </div>
            <div className="ambassador-event-fact" role="listitem">
              <strong>6개월</strong>
              <span>활동 기간</span>
            </div>
            <div className="ambassador-event-fact" role="listitem">
              <strong>PRO 1년</strong>
              <span>주요 혜택</span>
            </div>
          </div>

          <div className="ambassador-event-hero-action">
            {renderApplyButton('hero')}
            {recruitmentOpen && (
              <p>지원서는 새 창에서 열려요.</p>
            )}
          </div>

          <div aria-label="인스타그램 관련 콘텐츠" className="ambassador-event-social" role="group">
            {AMBASSADOR_EVENT.socialLinks.map((link, index) => (
              <a
                href={link.url}
                key={link.url}
                onClick={() => handleSocialClick(link, index)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {link.text}
                <span aria-hidden="true">↗</span>
              </a>
            ))}
          </div>
        </header>

        <section aria-labelledby="ambassador-cardnews-title" className="ambassador-event-cardnews">
          <div className="ambassador-event-section-header">
            <h2 id="ambassador-cardnews-title">활동과 혜택을 확인하세요</h2>
            <p>카드뉴스를 아래로 넘기며 자세히 볼 수 있어요.</p>
          </div>

          <div className="ambassador-event-cardnews-list">
            {AMBASSADOR_EVENT.slides.map((slide, index) => (
              <figure className="ambassador-event-cardnews-item" key={slide.image}>
                <img
                  alt={slide.alt}
                  decoding="async"
                  height="840"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  src={slide.image}
                  width="840"
                />
                <figcaption className="ambassador-event-visually-hidden">
                  카드뉴스 {index + 1}장: {slide.alt}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section aria-labelledby="ambassador-final-title" className="ambassador-event-final">
          <span>함께 만드는 변화</span>
          <h2 id="ambassador-final-title">선생님의 경험을 들려주세요</h2>
          <p>
            왓퀴즈를 직접 사용하고 나눈 의견이 더 나은 교육 도구를 만들어요.
          </p>
          {renderApplyButton('final')}
        </section>
      </main>
    </div>
  );
};

export default AmbassadorEventPage;
